import React from "react";
import {
	AssetFilename,
	AssetPickerLabel,
	AssetRow,
	AssetThumb,
	HiddenControl,
	OptionError,
	OptionField,
	OptionHint,
	OptionLegend,
	QuietButton,
} from "./optionControls.styles";

/**
 * One single-file creative: the advertiser's mark, a video, or a video's poster frame
 * (kitecorp/transferify-ui#1868).
 *
 * ONE component for all three because they differ only in DATA — which formats the picker offers, which
 * copy names the control, and whether the uploaded file has a still to show. Three components would be
 * three copies of the hidden-input trick and the reset-before-change rule below, and the first fix to
 * either would move one of them.
 *
 * Distinct from the multi-image creative picker on `SponsorPage`: that one is a set with an order the
 * sponsor edits, and this is a slot that holds exactly one thing. Replacing is picking again, which is
 * why there is no separate replace control.
 *
 * Dumb by mandate (epic #26): what is held, what may be picked, and two callbacks.
 */
export interface AssetPickerFieldProps {
	label: string;
	hint: string;
	/** The `accept` attribute, derived from the same list the validator judges against. */
	accept: string;
	/** Names the control for a screen reader; the visible text says "Add" and not what is added. */
	addAria: string;
	replaceAria: string;
	addLabel: string;
	removeLabel: string;
	/** The uploaded file's durable URL, or `null` when the slot is empty. */
	url: string | null;
	/**
	 * The stored file's alt text, or `null` for a file with no still to show — a video.
	 *
	 * `null` is what switches the preview from a thumbnail to the file's name: a `<video>` poster in a
	 * 52px box says nothing a filename does not, and decoding one to say it costs the advertiser a
	 * download of their own upload.
	 */
	alt: string | null;
	/** What to show in place of a thumbnail when {@link alt} is `null`. */
	filename: string | null;
	disabled: boolean;
	/** An upload is running or waiting for the restored sign-in (ui#3117): the input reports itself busy. */
	busy?: boolean;
	/** `opener` is this field's input, where a sign-in the pick opens returns focus (ui#3117). */
	onPick: (file: File, opener: HTMLElement) => void;
	onRemove: () => void;
	error: string | null;
}

export function AssetPickerField({
	label, hint, accept, addAria, replaceAria, addLabel, removeLabel,
	url, alt, filename, disabled, busy = false, onPick, onRemove, error,
}: AssetPickerFieldProps): React.ReactElement {
	const held = url !== null;
	return (
		<OptionField>
			<OptionLegend>{label}</OptionLegend>
			<OptionHint>{hint}</OptionHint>
			<AssetRow>
				{held && alt !== null && <AssetThumb src={url} alt={alt}/>}
				{held && alt === null && filename !== null && <AssetFilename>{filename}</AssetFilename>}
				<AssetPickerLabel $disabled={disabled}>
					<HiddenControl
						type="file"
						accept={accept}
						aria-label={held ? replaceAria : addAria}
						disabled={disabled}
						aria-busy={busy}
						onChange={event => {
							const picked = event.target.files?.[0];
							// Reset FIRST: picking the same file twice fires no change event otherwise, so
							// a refused upload could not be retried with the very file the advertiser just
							// fixed.
							event.target.value = "";
							if (picked !== undefined) {
								onPick(picked, event.currentTarget);
							}
						}}
					/>
					{addLabel}
				</AssetPickerLabel>
				{held && (
					<QuietButton disabled={disabled} onClick={onRemove}>{removeLabel}</QuietButton>
				)}
			</AssetRow>
			{error !== null && <OptionError role="alert">{error}</OptionError>}
		</OptionField>
	);
}
